/**
 * Case resume page.
 *
 * Allows an applicant to look up an existing verification case by
 * reference and resume where they left off. The page calls
 * `GET /v1/applicant/cases/:caseId` and routes the applicant to the
 * appropriate step based on the server-computed `nextStep` directive.
 *
 * @module pages/CaseResumePage
 */

import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ApiClientError } from '@noxverify/api-client';
import type { ApplicantContext } from '../modules/context/context-loader';
import { getApiClient } from '../lib/api';
import { Footer } from '../components/Footer';

interface CaseResumePageProps {
  context: ApplicantContext;
}

export function CaseResumePage({ context }: CaseResumePageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('portal');
  const [reference, setReference] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const validationError =
    touched && !reference.trim()
      ? t('caseResume.errors.missingReference')
      : null;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setTouched(true);

      const trimmed = reference.trim();
      if (!trimmed) return;

      setLoading(true);
      setError(null);

      try {
        const client = getApiClient();
        const res = await client.applicant.getCase(trimmed);
        const caseData = res.data;

        // Route based on the server-computed next step.
        switch (caseData.nextStep.action) {
          case 'upload_documents': {
            const collectingSet = caseData.submissionSets.find(
              (ss) => ss.status === 'collecting',
            );
            navigate(
              `/case/${caseData.id}/documents${collectingSet ? `?ss=${collectingSet.id}` : ''}`,
            );
            break;
          }
          case 'resubmit': {
            const collectingSet = caseData.submissionSets.find(
              (ss) => ss.status === 'collecting',
            );
            navigate(
              `/resubmit?caseId=${caseData.id}${collectingSet ? `&ss=${collectingSet.id}` : ''}`,
            );
            break;
          }
          case 'wait':
          case 'complete':
            navigate(`/case/${caseData.id}/confirmation`);
            break;
          case 'restart':
            setError(t('caseResume.errors.expired'));
            break;
          case 'contact_support':
            setError(t('caseResume.errors.suspended'));
            break;
          default:
            navigate(`/case/${caseData.id}/confirmation`);
        }
      } catch (err: unknown) {
        const apiErr = err as ApiClientError;
        if (apiErr.status === 404) {
          setError(t('caseResume.errors.notFound'));
        } else {
          setError(t('caseResume.errors.lookupFailed'));
        }
      } finally {
        setLoading(false);
      }
    },
    [reference, navigate, t],
  );

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      {/* Header */}
      <header className="border-b border-hairline bg-surface">
        <div className="mx-auto flex max-w-3xl items-center gap-4 px-4 py-4">
          {context.branding.logoUrl && (
            <img
              src={context.branding.logoUrl}
              alt={t('app.logoAlt', { projectSlug: context.projectSlug })}
              className="h-8"
            />
          )}
          <h1
            className="text-lg font-semibold"
            style={{ color: 'var(--nv-primary-color)' }}
          >
            {t('caseResume.headerTitle')}
          </h1>
        </div>
      </header>

      {/* Main content */}
      <main className="flex flex-1 items-center justify-center px-4 py-12">
        <div className="w-full max-w-md rounded-lg border border-hairline bg-surface-raised p-8">
          <h2 className="mb-2 text-center text-xl font-bold text-ink">
            {t('caseResume.title')}
          </h2>
          <p className="mb-6 text-center text-sm text-ink-muted">
            {t('caseResume.description')}
          </p>

          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-4">
              <label
                htmlFor="case-reference"
                className="mb-1 block text-sm font-medium text-ink-muted"
              >
                {t('caseResume.referenceLabel')}
              </label>
              <input
                id="case-reference"
                type="text"
                value={reference}
                onChange={(e) => {
                  setReference(e.target.value);
                  if (error) setError(null);
                }}
                onBlur={() => setTouched(true)}
                placeholder={t('caseResume.referencePlaceholder')}
                aria-invalid={!!(validationError || error)}
                aria-describedby={
                  validationError
                    ? 'ref-validation-error'
                    : error
                      ? 'ref-api-error'
                      : undefined
                }
                disabled={loading}
                className={[
                  'w-full rounded-lg border px-4 py-2 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 disabled:opacity-50',
                  validationError || error
                    ? 'border-danger/40 focus:ring-danger/30'
                    : 'border-hairline-strong focus:ring-[var(--nv-primary-color)]',
                ].join(' ')}
              />
              {validationError && (
                <p
                  id="ref-validation-error"
                  role="alert"
                  className="mt-1 text-sm text-danger"
                >
                  {validationError}
                </p>
              )}
            </div>

            {error && (
              <div
                id="ref-api-error"
                role="alert"
                className="mb-4 rounded-lg border border-danger/30 bg-danger-tint px-4 py-3 text-sm text-danger"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-md px-6 py-3 font-semibold text-white transition-opacity hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50"
              style={{
                backgroundColor: 'var(--nv-primary-color)',
                '--tw-ring-color': 'var(--nv-primary-color)',
              } as React.CSSProperties}
            >
              {loading && (
                <span
                  className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                  aria-hidden="true"
                />
              )}
              {loading ? t('caseResume.looking') : t('caseResume.continue')}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-ink-muted">
            <a
              href="/"
              className="underline hover:text-ink-muted"
            >
              {t('caseResume.startNew')}
            </a>
          </p>
        </div>
      </main>

      <Footer branding={context.branding} />
    </div>
  );
}
